import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, ArrowRight } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Container } from '../components/layout/Container';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { getStateByCode } from '../config/states';
import { getTotalItemsForState } from '../config/checklist';
import { resolveDripInvite, startFromInvite } from '../lib/api';

/**
 * Personalized invite landing for campaign emails (/i/:token).
 * The token resolves to the lead's email + state server-side, so this page
 * asks for nothing — one explicit click starts the assessment. The click is
 * deliberate: email security scanners open links, so page load must never
 * create anything. Only the button press counts as a human start.
 */
export function Invite() {
  const { inviteToken } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const campaign = searchParams.get('utm_campaign');

  const [phase, setPhase] = useState<'loading' | 'ready' | 'invalid'>('loading');
  const [leadState, setLeadState] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!inviteToken) {
      setPhase('invalid');
      return;
    }
    let cancelled = false;
    resolveDripInvite(inviteToken, campaign)
      .then((res) => {
        if (cancelled) return;
        setLeadState(res.state);
        setLeadEmail(res.email);
        setPhase('ready');
      })
      .catch(() => {
        if (!cancelled) setPhase('invalid');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteToken]);

  const stateInfo = leadState ? getStateByCode(leadState) : null;
  const stateName = stateInfo?.name || 'your state';

  const handleStart = async () => {
    if (!inviteToken || isStarting) return;
    setIsStarting(true);
    setStartError('');
    try {
      const itemCount = leadState ? getTotalItemsForState(leadState) : undefined;
      const result = await startFromInvite(inviteToken, campaign, itemCount);
      navigate(`/assessment?snapshotId=${result.snapshotId}`);
    } catch {
      setStartError('Something went wrong starting your assessment. Please try again.');
      setIsStarting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-1 py-8 pt-24 md:py-12 md:pt-28">
        <Container size="md">
          {phase === 'loading' && (
            <div className="text-center py-20 text-gray-500">Loading your snapshot…</div>
          )}

          {phase === 'invalid' && (
            <Card className="overflow-hidden max-w-xl mx-auto">
              <CardContent className="p-6 sm:p-8 text-center">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
                  This link has expired
                </h1>
                <p className="text-gray-600 mb-6">
                  No problem — the free Compliance Risk Snapshot is still open to you.
                  It takes about 10 minutes and the full report is yours to keep.
                </p>
                <Button size="lg" onClick={() => navigate('/checkout')} rightIcon={<ArrowRight className="w-5 h-5" />}>
                  Start My Free Assessment
                </Button>
              </CardContent>
            </Card>
          )}

          {phase === 'ready' && (
            <Card className="overflow-hidden max-w-xl mx-auto">
              <div className="text-white px-4 sm:px-6 py-5 sm:py-7 text-center" style={{ backgroundColor: '#009688' }}>
                <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center mx-auto mb-3">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold mb-1">
                  Your {stateName} compliance snapshot is ready
                </h1>
                <p className="text-sm sm:text-base" style={{ color: 'rgba(255,255,255,0.85)' }}>
                  Set up for you — no forms to fill out
                </p>
              </div>

              <CardContent className="p-5 sm:p-8">
                <ul className="space-y-3 mb-6">
                  {[
                    `Checks the same personnel-file items a ${stateInfo?.regulatoryBodyAbbr || 'state'} surveyor reviews`,
                    'About 10 minutes — your risk score appears the moment you finish',
                    'Full PDF report with regulation citations, free',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm sm:text-base text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className="w-full"
                  size="lg"
                  onClick={handleStart}
                  isLoading={isStarting}
                  leftIcon={<Shield className="w-5 h-5" />}
                >
                  Start My Free Assessment
                </Button>

                {startError && (
                  <p className="mt-3 text-sm text-center text-red-600">{startError}</p>
                )}

                <p className="mt-3 text-xs text-center text-gray-500">
                  No payment required. Your report goes to <strong>{leadEmail}</strong>.
                </p>
              </CardContent>
            </Card>
          )}
        </Container>
      </main>

      <Footer />
    </div>
  );
}
